import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, collection, getDocs, orderBy, query } from 'firebase/firestore';
import { PFIFullLogo } from '../components/common/PFILogo';
import { FlamingoLogo } from '../components/common/FlamingoBrand';
import CustomerGanttChart from '../components/CustomerGanttChart';
import { formatProjectName } from '../utils/projectUtils';
import { 
  CalendarDaysIcon,
  ClockIcon,
  CheckCircleIcon,
  CurrencyDollarIcon,
  PhoneIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  PhotoIcon,
  UserIcon,
  BuildingStorefrontIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  HomeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';

export default function CustomerTimelinePage() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load project
      const projectDoc = await getDoc(doc(db, 'projects', id));
      if (projectDoc.exists()) {
        const projectData = { id: projectDoc.id, ...projectDoc.data() };
        setProject(projectData);
        
        // Load milestones from project document
        if (projectData.milestones) {
          setMilestones(projectData.milestones);
        }
        
        // Load tasks for this project
        const tasksSnapshot = await getDocs(
          query(collection(db, 'projects', id, 'tasks'), orderBy('createdAt', 'asc'))
        );
        const tasksData = tasksSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTasks(tasksData);
      } else {
        setError('Project not found');
      }
      
    } catch (error) {
      console.error('Error loading project:', error);
      setError('Failed to load project details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';
    const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return dateObj.toLocaleDateString('en-AU', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatShortDate = (date) => {
    if (!date) return 'Not set';
    const dateObj = date.seconds ? new Date(date.seconds * 1000) : new Date(date);
    return dateObj.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getTimelineEvents = () => {
    const events = [];

    // Project start event
    if (project?.createdAt) {
      events.push({
        id: 'project-start',
        type: 'project-milestone',
        title: 'Project Started',
        description: 'Initial consultation and project planning phase begun',
        date: project.createdAt,
        icon: HomeIcon,
        color: 'text-green-600',
        bgColor: 'bg-green-100',
        completed: true
      });
    }

    // Task events
    tasks.forEach(task => {
      if (task.createdAt) {
        events.push({
          id: `task-created-${task.id}`,
          type: 'task-created',
          title: `Task Created: ${task.name}`,
          description: task.description || 'New task added to project',
          date: task.createdAt,
          icon: DocumentTextIcon,
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          completed: true,
          task: task
        });
      }

      if (task.status === 'completed' && task.completedAt) {
        events.push({
          id: `task-completed-${task.id}`,
          type: 'task-completed',
          title: `Task Completed: ${task.name}`,
          description: 'Task has been successfully completed',
          date: task.completedAt,
          icon: CheckCircleIcon,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          completed: true,
          task: task
        });
      }
    });

    // Expected completion event (future)
    if (project?.expectedEndDate) {
      const endDate = project.expectedEndDate.seconds ? 
        new Date(project.expectedEndDate.seconds * 1000) : new Date(project.expectedEndDate);
      const now = new Date();
      
      events.push({
        id: 'project-completion',
        type: 'project-milestone',
        title: 'Expected Completion',
        description: 'Final installation and project completion',
        date: project.expectedEndDate,
        icon: SparklesIcon,
        color: endDate < now ? 'text-red-600' : 'text-purple-600',
        bgColor: endDate < now ? 'bg-red-100' : 'bg-purple-100',
        completed: false,
        isOverdue: endDate < now
      });
    }

    // Sort events by date
    return events.sort((a, b) => {
      const dateA = a.date.seconds ? new Date(a.date.seconds * 1000) : new Date(a.date);
      const dateB = b.date.seconds ? new Date(b.date.seconds * 1000) : new Date(b.date);
      return dateA - dateB;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-25 to-orange-25 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading project timeline...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-25 to-orange-25 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <PFIFullLogo size="md" className="mx-auto mb-6" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Project Timeline Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The project timeline you are looking for could not be found.'}</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-blue-800 text-sm">
              If you believe this is an error, please contact Pink Flamingo Interiors directly.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const timelineEvents = getTimelineEvents();
  const completedTasks = tasks.filter(task => task.status === 'completed').length;
  const progress = tasks.length > 0 ? Math.round((completedTasks / tasks.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-25 to-orange-25">
      {/* Header */}
      <div className="relative bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <path d="M0,0 Q50,30 100,0 L100,100 L0,100 Z" fill="white"/>
          </svg>
        </div>
        <div className="relative px-6 py-12">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <PFIFullLogo size="md" className="text-white" />
              <div className="flex items-center space-x-4">
                <Link
                  to={`/customer/project/${id}`}
                  className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
                >
                  <HomeIcon className="w-4 h-4 mr-2" />
                  Project Overview
                </Link>
                <a
                  href="mailto:hello@pinkflamingointeriors.com.au"
                  className="inline-flex items-center px-4 py-2 bg-white bg-opacity-20 text-gray-900 rounded-lg hover:bg-opacity-30 transition-all duration-300"
                >
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Contact Us
                </a>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
              <div className="lg:col-span-3">
                <h1 className="text-4xl font-bold mb-4">{formatProjectName(project)} - Timeline</h1>
                <p className="text-purple-100 text-xl mb-6">
                  Track the detailed progress of your interior design project
                </p>
                
                <div className="flex items-center space-x-6 text-purple-100">
                  <div className="flex items-center">
                    <CalendarDaysIcon className="w-5 h-5 mr-2" />
                    {timelineEvents.length} Timeline Events
                  </div>
                  <div className="flex items-center">
                    <CheckCircleIcon className="w-5 h-5 mr-2" />
                    {completedTasks} of {tasks.length} Tasks Complete
                  </div>
                </div>
              </div>

              <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-2xl p-6 border border-white border-opacity-20">
                <div className="text-center">
                  <div className="text-3xl font-bold text-white mb-2">{progress}%</div>
                  <div className="text-purple-100 mb-4">Project Progress</div>
                  <div className="w-full bg-white bg-opacity-20 rounded-full h-3">
                    <div 
                      className="bg-white rounded-full h-3 transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Gantt Chart */}
        <div className="mb-8">
          <CustomerGanttChart 
            project={project}
            milestones={milestones}
            tasks={tasks}
            isAdmin={false}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Legacy Timeline View */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6">
              <div className="flex items-center mb-6">
                <CalendarDaysIcon className="w-6 h-6 text-purple-600 mr-3" />
                <h3 className="text-xl font-bold text-gray-900">Milestone Details</h3>
              </div>

              {milestones.filter(m => m.visibleToCustomer !== false).length > 0 ? (
                <div className="space-y-4">
                  {milestones
                    .filter(m => m.visibleToCustomer !== false)
                    .sort((a, b) => {
                      const dateA = a.date.toDate ? a.date.toDate() : new Date(a.date);
                      const dateB = b.date.toDate ? b.date.toDate() : new Date(b.date);
                      return dateA - dateB;
                    })
                    .map((milestone) => (
                      <div key={milestone.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center mb-2">
                              {milestone.completed && (
                                <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
                              )}
                              <h4 className="font-semibold text-gray-900">{milestone.title}</h4>
                            </div>
                            {milestone.description && (
                              <p className="text-gray-600 text-sm mb-2">{milestone.description}</p>
                            )}
                            {milestone.images && milestone.images.length > 0 && (
                              <div className="mt-2">
                                <div className="flex items-center text-sm text-blue-600 mb-2">
                                  <PhotoIcon className="w-4 h-4 mr-1" />
                                  {milestone.images.length} progress photo{milestone.images.length > 1 ? 's' : ''}
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <div className="text-sm font-medium text-gray-900">
                              {formatShortDate(milestone.date)}
                            </div>
                            {milestone.completed && (
                              <span className="inline-block mt-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                Completed
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CalendarDaysIcon className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                  <p>Milestones will appear here as your project progresses</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Quick Message */}
            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                <ChatBubbleLeftRightIcon className="w-5 h-5 mr-2 text-purple-600" />
                Message PFI Team
              </h3>
              
              <p className="text-gray-600 text-sm mb-4">
                Have a question about your project? Send us a quick message!
              </p>
              
              <div className="space-y-3">
                <Link
                  to={`/customer/inbox/${id}`}
                  className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                  Open Messages
                </Link>
                <div className="text-xs text-gray-500 text-center">
                  All project communications in one place
                </div>
              </div>
            </div>
            {/* Project Summary */}
            <div className="bg-white rounded-2xl shadow-xl border border-purple-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Project Summary</h3>
              
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">Status</span>
                  <span className="font-semibold capitalize">{project.status || 'Planning'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Start Date</span>
                  <span className="font-semibold">{formatShortDate(project.createdAt)}</span>
                </div>
                {project.expectedEndDate && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Due Date</span>
                    <span className="font-semibold">{formatShortDate(project.expectedEndDate)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Total Tasks</span>
                  <span className="font-semibold">{tasks.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Completed</span>
                  <span className="font-semibold text-green-600">{completedTasks}</span>
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-2xl shadow-xl border border-pink-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Need Help?</h3>
              
              <div className="space-y-3">
                <a
                  href="tel:+61400000000"
                  className="w-full flex items-center justify-center px-4 py-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <PhoneIcon className="w-4 h-4 mr-2" />
                  Call Us Now
                </a>
                <a
                  href="mailto:hello@pinkflamingointeriors.com.au"
                  className="w-full flex items-center justify-center px-4 py-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <EnvelopeIcon className="w-4 h-4 mr-2" />
                  Send Email
                </a>
                <Link
                  to={`/customer/inbox/${id}`}
                  className="w-full flex items-center justify-center px-4 py-3 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition-colors"
                >
                  <ChatBubbleLeftRightIcon className="w-4 h-4 mr-2" />
                  Send Message
                </Link>
              </div>
            </div>

            {/* Task Status Breakdown */}
            <div className="bg-white rounded-2xl shadow-xl border border-orange-100 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Task Status</h3>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">Completed</span>
                  </div>
                  <span className="font-semibold text-green-600">{tasks.filter(t => t.status === 'completed').length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">In Progress</span>
                  </div>
                  <span className="font-semibold text-blue-600">{tasks.filter(t => t.status === 'in_progress').length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                    <span className="text-sm text-gray-600">Pending</span>
                  </div>
                  <span className="font-semibold text-orange-600">{tasks.filter(t => t.status === 'pending' || !t.status).length}</span>
                </div>
              </div>
            </div>

            {/* Refresh */}
            <div className="bg-white rounded-2xl shadow-xl border border-green-100 p-6">
              <button
                onClick={() => {
                  setLoading(true);
                  loadData();
                }}
                className="w-full flex items-center justify-center px-4 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                disabled={loading}
              >
                <ArrowPathIcon className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Refreshing...' : 'Refresh Timeline'}
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-2 text-gray-500">
            <FlamingoLogo size="sm" className="text-pink-500" />
            <span className="text-sm">"Fun and Fabulous without the Fuss" ✨</span>
          </div>
        </div>
      </div>
    </div>
  );
}
